import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ShopHeroTemplateBlockConfig } from '@front-platform/content-builder';
import { shopHeroTemplateBlockResolveConfig } from '@front-platform/content-builder';
import { Component, DestroyRef, inject, input } from '@angular/core';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { map } from 'rxjs';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { IDistributionMode } from '@core/models/shop.model';
import { ParametersUtils } from '@shared/utils/parameters/parameters.utils';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { Router } from '@angular/router';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { SettingsParametersState } from '@store/settings';
import { SettingsStoreDispatcher } from '@common/dispatchers/settings.dispatcher';
import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';
import { CartKeys } from '@config/keys/cart.keys';
import { AnalyticsService } from '@core/services/app/analytics.service';
export interface ShopHeroTemplateBlockRuntimeConfig {
  'carousel_card_color'?: IContentBuilderFieldColor | undefined;
  'cards'?: [] | undefined;
}

export type ShopHeroTemplateBlockConfigInput = Partial<
  Omit<ShopHeroTemplateBlockConfig, keyof ShopHeroTemplateBlockRuntimeConfig> &
    ShopHeroTemplateBlockRuntimeConfig
>;
export type ShopHeroTemplateBlockResolvedConfig = Omit<
  ShopHeroTemplateBlockConfig,
  keyof ShopHeroTemplateBlockRuntimeConfig
> &
  Partial<ShopHeroTemplateBlockRuntimeConfig>;
export interface ShopHeroTemplateBlockContract extends ConfigComponentContract<
  ShopHeroTemplateBlockConfigInput,
  ShopHeroTemplateBlockResolvedConfig
> {}

@Component({
  selector: 'app-shop-hero-template-block',
  templateUrl: './shop-hero-template-block.component.html',
  styles: [
    `
      @media (min-width: 1024px) and (min-height: 820px) {
        .lg-and-min-height-820-hover-upper-container {
          @apply absolute;
          @apply bottom-0;
        }
      }
    `,
  ],
  standalone: false,
})
export class ShopHeroTemplateBlockComponent implements ShopHeroTemplateBlockContract {
  readonly config = input<ShopHeroTemplateBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    ShopHeroTemplateBlockConfig,
    ShopHeroTemplateBlockRuntimeConfig
  >(shopHeroTemplateBlockResolveConfig, () => ({ config: this.config() }));
  get displayNavigation(): boolean {
    return this.resolvedConfig()['display_navigation'] as unknown as boolean;
  }
  get navigationLogo(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['navigation_logo'] as unknown as
      IContentBuilderFieldImage | undefined;
  }
  get accentColor(): ContentBuilderFieldColors | undefined {
    return this.resolvedConfig()['accent_color'] as unknown as
      ContentBuilderFieldColors | undefined;
  }

  get redirectUrl(): string | undefined {
    return this.resolvedConfig()['redirect_url'] as unknown as string | undefined;
  }

  get heroImage(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['hero_image'] as unknown as IContentBuilderFieldImage | undefined;
  }
  get heroLogo(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['hero_logo'] as unknown as IContentBuilderFieldImage | undefined;
  }
  get heroBaseline(): string | undefined {
    return this.resolvedConfig()['hero_baseline'] as unknown as string | undefined;
  }
  get heroBaselineColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['hero_baseline_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get heroDistributionButtonColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['hero_distribution_button_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get heroDistributionButtonBackgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['hero_distribution_button_background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get distributionModesCardsOptions():
    | {
        width: number;
        height: number;
      }
    | undefined {
    return this.resolvedConfig()['distribution_modes_cards_options'] as unknown as
      | {
          width: number;
          height: number;
        }
      | undefined;
  }

  get heroContextualHeading(): string | undefined {
    return this.resolvedConfig()['hero_contextual_heading'] as unknown as string | undefined;
  }
  get heroContextualHeadingColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['hero_contextual_heading_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get heroContextualContent(): string | undefined {
    return this.resolvedConfig()['hero_contextual_content'] as unknown as string | undefined;
  }

  get displayCarousel(): boolean {
    return this.resolvedConfig()['display_carousel'] as unknown as boolean;
  }
  get carouselCardColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['carousel_card_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }

  get cards(): [] | undefined {
    return this.resolvedConfig()['cards'] as unknown as [] | undefined;
  }

  destroyRef = inject(DestroyRef);

  availableDistributionModes$ = this.marketplaceSelector.selectMarketplaceDistributionModes.pipe(
    map(data => data.filter(distributionMode => distributionMode.enabled)),
    map((distributions: IDistributionMode[]) =>
      ParametersUtils.mapDistributionModes(distributions),
    ),
  );

  settingsParameters$? = this.settingsSelector.selectParameters$;

  constructor(
    private readonly marketplaceSelector: MarketplaceStoreSelector,
    private readonly settingsSelector: SettingsStoreSelector,
    private readonly router: Router,
    private readonly routerHelper: RouterHelperService,
    private readonly settingsStoreDispatcher: SettingsStoreDispatcher,
    private readonly cartStoreDispatcher: CartStoreDispatcher,
    private readonly analyticsService: AnalyticsService,
  ) {}

  openParametersModal(distributionMode: string): void {
    const selectedDistributionModeParameters: Partial<SettingsParametersState> = {
      is_distribution_mode_defined: true,
      distribution_mode: distributionMode,
      last_update: new Date(),
    };

    this.settingsStoreDispatcher.updateParameters(selectedDistributionModeParameters);
    // update existing parameters, ! It may cause side effects, like if a slot was already used, maybe the slot is still in use but not appropriate
    this.cartStoreDispatcher.updateActive({
      [CartKeys.Type]: distributionMode,
    });

    this.redirectToAnotherPage();
  }

  redirectToAnotherPage(): void {
    if (this.redirectUrl) {
      this.redirectUrl.startsWith('http') || this.redirectUrl.startsWith('https')
        ? window.open(this.redirectUrl)
        : this.router.navigate([this.routerHelper.translateRoute(this.redirectUrl)]);
    }
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ShopHeroTemplateBlockComponent, { 'keyAliases': {} });
